import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Tooltip,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { useSelector } from "react-redux";
import { theme } from "../../Theme";
import { PROMO_COLORS } from "./constants/colors";
// import { promoSimulatorService } from "./services/promoSimulatorService"; 
import { normalizeWarnings, WarningBadge } from "./components/WarningsSection";

const COMPARE_SOURCE = {
  // recommended: "recommended", 
  myScenarios: "my_scenarios",
};

function fmtFixed(val, decimals = 2) {
  if (val === undefined || val === null || Number.isNaN(Number(val))) {
    return "—";
  }
  return Number(val).toFixed(decimals);
}

function derivedProfitLegacy(scenario) {
  const p = scenario.kpis?.profit;
  if (p != null && !Number.isNaN(p)) return p;
  const inc = scenario.kpis?.incrementalSales ?? 0;
  const m = scenario.kpis?.grossMarginImpact ?? 0;
  return Math.round(inc * 0.22 + m * 9000);
}

function profitForSort(s) {
  if (s.kpis?.expectedProfit != null && !Number.isNaN(Number(s.kpis.expectedProfit))) {
    return Number(s.kpis.expectedProfit);
  }
  return derivedProfitLegacy(s);
}

function derivedVolumeLegacy(scenario) {
  const v = scenario.kpis?.volumeUnits;
  if (v != null && !Number.isNaN(v)) return v;
  const base = scenario.kpis?.baseSales ?? 0;
  const inc = scenario.kpis?.incrementalSales ?? 0;
  return Math.round(base / 50 + inc / 40);
}

function volumeForSort(s) {
  if (s.kpis?.expectedVolumeL != null && !Number.isNaN(Number(s.kpis.expectedVolumeL))) {
    return Number(s.kpis.expectedVolumeL);
  }
  return derivedVolumeLegacy(s);
}

function roiForSort(s) {
  const r = s.kpis?.roi;
  if (r != null && !Number.isNaN(Number(r))) return Number(r);
  return -Infinity;
}

function maxMetric(rows, getter) {
  let max = -Infinity;
  rows.forEach((row) => {
    const v = getter(row);
    if (typeof v === "number" && !Number.isNaN(v)) max = Math.max(max, v);
  });
  return max === -Infinity ? null : max;
}

function idsAtMax(rows, getter, maxVal) {
  if (maxVal == null) return new Set();
  return new Set(
    rows
      .filter((row) => getter(row) === maxVal)
      .map((row) => row.id)
  );
}

/*
 * COMMENTED: mapCompareApiResultToColumn — only used for recommended discount API results
 *
function mapCompareApiResultToColumn(r, idx, fallbackSkuName) {
  const d = typeof r.discount === "number" ? r.discount : null;
  const discountPct =
    d != null ? Math.round(d * 10000) / 100 : null;
  const skuName =
    (typeof r.sku_name === "string" && r.sku_name.trim()) ||
    (typeof r.skuName === "string" && r.skuName.trim()) ||
    (typeof fallbackSkuName === "string" && fallbackSkuName.trim()) ||
    null;
  return {
    id: `api_compare_${idx}_${d ?? idx}`,
    name: discountPct != null ? `${discountPct}% discount` : `Option ${idx + 1}`,
    discountPct,
    kpis: {
      expectedVolumeL: r.expected_volume_l,
      upliftRatio: r.uplift_ratio,
      expectedProfit: r.expected_profit,
      discountSpend: r.discount_spend,
      roi: r.roi,
      promoPricePerLiter: r.promo_price_pl,
      promoPriceUnit: r.promo_price_unit,
      skuName,
    },
    warnings: Array.isArray(r.warnings) ? r.warnings : [],
    compareSource: COMPARE_SOURCE.recommended,
    compareRank: idx,
  };
}
*/

function skuLabelForComparison(s) {
  if (Array.isArray(s.skus) && s.skus.length) {
    return s.skus.filter(Boolean).join(", ");
  }
  if (typeof s.skus === "string" && s.skus.trim()) {
    return s.skus.trim();
  }
  const fromKpis = s.kpis?.skuName;
  if (typeof fromKpis === "string" && fromKpis.trim()) {
    return fromKpis.trim();
  }
  return null;
}

function scenarioCompareChip(scenario, index, source) {
  /* rankByLabel, allScenarios removed — only used by commented recommended discount logic */
  /*
   * COMMENTED: recommended discount first-place chip
   *
  if (source === COMPARE_SOURCE.recommended && index === 0) {
    return (
      <Chip
        size="small"
        icon={<StarRoundedIcon sx={{ fontSize: "16px !important" }} />}
        label={`Recommended`}
        sx={{
          mt: 0.75,
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: `${PROMO_COLORS.safe}22`,
          color: PROMO_COLORS.safe,
          "& .MuiChip-icon": { color: PROMO_COLORS.safe },
        }}
      />
    );
  }
  */

  const explicit = scenario.compareIndicator;
  if (explicit === "recommended") {
    return (
      <Chip
        size="small"
        icon={<StarRoundedIcon sx={{ fontSize: "16px !important" }} />}
        label="Recommended"
        sx={{
          mt: 0.75,
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: `${PROMO_COLORS.safe}22`,
          color: PROMO_COLORS.safe,
          "& .MuiChip-icon": { color: PROMO_COLORS.safe },
        }}
      />
    );
  }

  if (source === COMPARE_SOURCE.myScenarios) {
    return null;
  }

  /*
   * COMMENTED: recommended discount best-ROI chip (unreachable when source is always myScenarios)
   *
  const roiMax = maxMetric(allScenarios, (s) =>
    s.kpis?.roi != null && !Number.isNaN(Number(s.kpis.roi))
      ? Number(s.kpis.roi)
      : null
  );
  const roi = scenario.kpis?.roi;
  const bestRoi =
    roiMax != null &&
    roi != null &&
    Number(roi) === roiMax &&
    !(scenario.warnings || []).length;
  if (bestRoi) {
    return (
      <Chip
        size="small"
        icon={<StarRoundedIcon sx={{ fontSize: "16px !important" }} />}
        label="Recommended"
        sx={{
          mt: 0.75,
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          backgroundColor: `${PROMO_COLORS.safe}22`,
          color: PROMO_COLORS.safe,
          "& .MuiChip-icon": { color: PROMO_COLORS.safe },
        }}
      />
    );
  }
  */

  return null;
}

function MetricRow({
  label,
  scenarios,
  accessor,
  unit = "",
  formatter,
  highlightScenarioIds,
  tooltip,
}) {
  return (
    <TableRow hover>
      <TableCell
        sx={{
          fontWeight: 600,
          fontSize: 12,
          color: "text.secondary",
          whiteSpace: "nowrap",
          minWidth: 160,
        }}
      >
        {tooltip ? (
          <Tooltip title={tooltip} arrow placement="top-start">
            <Box component="span" sx={{ borderBottom: "1px dotted", cursor: "help" }}>
              {label}
            </Box>
          </Tooltip>
        ) : (
          label
        )}
      </TableCell>
      {scenarios.map((s) => {
        const val = accessor(s);
        let display = "—";
        if (val !== undefined && val !== null) {
          if (typeof val === "string") {
            display = val || "—";
          } else if (!Number.isNaN(val)) {
            display = `${formatter ? formatter(val) : val}${unit}`;
          }
        }
        const hi = highlightScenarioIds?.has(s.id);
        return (
          <TableCell
            key={s.id}
            sx={{
              textAlign: "center",
              fontSize: 13,
              fontWeight: hi ? 700 : 500,
              maxWidth: 220,
              whiteSpace: "normal",
              wordBreak: "break-word",
              ...(hi && {
                backgroundColor: `${PROMO_COLORS.safe}12`,
                borderRadius: 0.75,
                boxShadow: `inset 0 0 0 1px ${PROMO_COLORS.safe}66`,
              }),
            }}
          >
            {display}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/*
 * COMMENTED: SOURCE_OPTIONS — only needed when recommended discount source is available
 *
const SOURCE_OPTIONS = [
  { value: COMPARE_SOURCE.recommended, label: "Recommended discounts" },
  { value: COMPARE_SOURCE.myScenarios, label: "My scenarios" },
];
*/

/*
 * COMMENTED: RANK_BY_OPTIONS — only needed for recommended discount ranking
 *
const RANK_BY_OPTIONS = [
  { value: "roi", label: "ROI" },
  { value: "profit", label: "Profit" },
  { value: "volume", label: "Volume" },
];
*/

const MY_SORT_OPTIONS = [
  { value: "order", label: "Scenario order" },
  { value: "roi", label: "ROI (high → low)" },
  { value: "profit", label: "Profit (high → low)" },
  { value: "volume", label: "Volume (high → low)" },
];

export default function ScenarioComparison() {
  const reduxScenarios = useSelector((state) => state.promoSimulator.scenarios);

  // Fixed to myScenarios — recommended discount source is disabled
  const compareSource = COMPARE_SOURCE.myScenarios;
  /* const [compareSource, setCompareSource] = useState(COMPARE_SOURCE.recommended); */ // COMMENTED: recommended source
  /* const [recommendedRankBy, setRecommendedRankBy] = useState("roi"); */             // COMMENTED: recommended rank
  /* const [recommendedColumns, setRecommendedColumns] = useState([]); */              // COMMENTED: recommended columns
  /* const [recommendedLoading, setRecommendedLoading] = useState(false); */           // COMMENTED: recommended loading
  /* const [recommendedError, setRecommendedError] = useState(null); */                // COMMENTED: recommended error

  const [mySortBy, setMySortBy] = useState("order");
  const [highlightBestRoi, setHighlightBestRoi] = useState(true);
  const [highlightBestProfit, setHighlightBestProfit] = useState(true);

  /*
   * COMMENTED: useEffect for fetching recommended discount comparison from API
   *
  useEffect(() => {
    if (compareSource !== COMPARE_SOURCE.recommended) return undefined;
    let cancelled = false;
    setRecommendedLoading(true);
    setRecommendedError(null);
    promoSimulatorService
      .comparePromoDiscounts({ rank_by: recommendedRankBy })
      .then((payload) => {
        if (cancelled) return;
        const cols = (payload.results || []).map((r, idx) =>
          mapCompareApiResultToColumn(r, idx, payload.sku_name)
        );
        setRecommendedColumns(cols);
      })
      .catch((e) => {
        if (!cancelled) {
          setRecommendedError(e?.message || "Failed to load comparison");
          setRecommendedColumns([]);
        }
      })
      .finally(() => {
        if (!cancelled) setRecommendedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [compareSource, recommendedRankBy]);
  */

  const myScenarioColumns = useMemo(
    () =>
      reduxScenarios.map((s) => ({
        ...s,
        compareSource: COMPARE_SOURCE.myScenarios,
      })),
    [reduxScenarios]
  );

  const orderedScenarios = useMemo(() => {
    /*
     * COMMENTED: recommended discount branch
     *
    if (compareSource === COMPARE_SOURCE.recommended) {
      return recommendedColumns;
    }
    */
    const list = [...myScenarioColumns];
    if (mySortBy === "order") return list;
    const getter =
      mySortBy === "roi"
        ? roiForSort
        : mySortBy === "profit"
          ? profitForSort
          : volumeForSort;
    list.sort((a, b) => getter(b) - getter(a));
    return list;
  }, [
    myScenarioColumns,
    mySortBy,
  ]);

  /*
   * COMMENTED: rankByLabel — only used for recommended discount column labeling
   *
  const rankByLabel =
    RANK_BY_OPTIONS.find((o) => o.value === recommendedRankBy)?.label ??
    recommendedRankBy;
  */

  const roiMax = maxMetric(orderedScenarios, (s) =>
    s.kpis?.roi != null && !Number.isNaN(Number(s.kpis.roi))
      ? Number(s.kpis.roi)
      : null
  );
  const profitMax = maxMetric(orderedScenarios, profitForSort);

  const bestRoiIds = idsAtMax(
    orderedScenarios,
    (s) =>
      s.kpis?.roi != null && !Number.isNaN(Number(s.kpis.roi))
        ? Number(s.kpis.roi)
        : null,
    roiMax
  );
  const bestProfitIds = idsAtMax(orderedScenarios, profitForSort, profitMax);

  const metricRows = [
    {
      label: "Brand",
      accessor: (s) => {
        const b = s.brand ?? s.kpis?.brand;
        if (b == null || b === "") return "—";
        return typeof b === "string" ? b : String(b);
      },
    },
    {
      label: "SKU",
      accessor: (s) => skuLabelForComparison(s),
    },
    {
      label: "Discount",
      accessor: (s) => s.discountPct,
      formatter: fmtFixed,
      unit: "%",
    },
    {
      label: "Expected volume (L)",
      accessor: (s) => s.kpis?.expectedVolumeL,
      formatter: fmtFixed,
    },
    {
      label: "Uplift ratio",
      accessor: (s) => s.kpis?.upliftRatio,
      formatter: (v) => `${fmtFixed(v)}×`,
      unit: "",
    },
    {
      label: "Expected profit",
      accessor: (s) => s.kpis?.expectedProfit,
      formatter: fmtFixed,
      highlightScenarioIds: highlightBestProfit ? bestProfitIds : undefined,
    },
    {
      label: "Discount spend",
      accessor: (s) => s.kpis?.discountSpend,
      formatter: fmtFixed,
    },
    {
      label: "ROI",
      accessor: (s) => s.kpis?.roi,
      formatter: (v) => (v === null || v === undefined ? "—" : fmtFixed(v)),
      unit: "",
      highlightScenarioIds: highlightBestRoi ? bestRoiIds : undefined,
    },
    {
      label: "Promo price / L",
      accessor: (s) => s.kpis?.promoPricePerLiter,
      formatter: fmtFixed,
    },
    {
      label: "Promo price / unit",
      accessor: (s) => s.kpis?.promoPriceUnit,
      formatter: fmtFixed,
    },
  ];

  /*
   * COMMENTED: recommended discount loading early return
   *
  if (compareSource === COMPARE_SOURCE.recommended && recommendedLoading) {
    return (
      <Box py={8} display="flex" justifyContent="center" alignItems="center" gap={2}>
        <CircularProgress size={28} sx={{ color: theme.palette.primary.main }} />
        <Typography color="text.secondary">Loading recommended comparison…</Typography>
      </Box>
    );
  }
  */

  /*
   * COMMENTED: recommended discount error early return
   *
  if (compareSource === COMPARE_SOURCE.recommended && recommendedError) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color={theme.palette.primary.main} mb={2}>
          Scenario Comparison
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="compare-source-label">Comparison source</InputLabel>
            <Select
              labelId="compare-source-label"
              label="Comparison source"
              value={compareSource}
              onChange={(e) => setCompareSource(e.target.value)}
            >
              {SOURCE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="compare-rank-err-label">Rank by</InputLabel>
            <Select
              labelId="compare-rank-err-label"
              label="Rank by"
              value={recommendedRankBy}
              onChange={(e) => setRecommendedRankBy(e.target.value)}
            >
              {RANK_BY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Alert severity="error">{recommendedError}</Alert>
      </Box>
    );
  }
  */

  if (
    compareSource === COMPARE_SOURCE.myScenarios &&
    orderedScenarios.length === 0
  ) {
    return (
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color={theme.palette.primary.main}
          mb={2}
        >
          Scenario Comparison
        </Typography>
        <Box py={6} textAlign="center">
          <Typography color="text.secondary">
            No saved scenarios yet. Save a scenario above to compare them here.
          </Typography>
        </Box>
      </Box>
    );
  }

  /*
   * COMMENTED: recommended discount empty results early return
   *
  if (
    compareSource === COMPARE_SOURCE.recommended &&
    orderedScenarios.length < 1
  ) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color={theme.palette.primary.main} mb={2}>
          Scenario Comparison
        </Typography>
        <Typography color="text.secondary">No comparison results returned.</Typography>
      </Box>
    );
  }
  */

  return (
    <Box>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color={theme.palette.primary.main}
        mb={2}
      >
        Scenario Comparison
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.75, sm: 2 },
          borderRadius: 2,
          border: "1px solid #F2F4F7",
          backgroundColor: "#FAFBFC",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 2 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            flexWrap="wrap"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ flex: { md: "0 1 auto" } }}
          >
            {/*
             * COMMENTED: Comparison source dropdown — recommended discount source removed
             *
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": { borderRadius: 1.5, backgroundColor: "#fff" },
              }}
            >
              <InputLabel id="compare-source-label">Comparison source</InputLabel>
              <Select
                labelId="compare-source-label"
                label="Comparison source"
                value={compareSource}
                onChange={(e) => setCompareSource(e.target.value)}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            */}

            {/*
             * COMMENTED: Rank by dropdown — only for recommended discount source
             * compareSource === COMPARE_SOURCE.recommended branch removed
             */}
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fff",
                },
              }}
            >
              <InputLabel id="compare-sort-label">Sort columns</InputLabel>
              <Select
                labelId="compare-sort-label"
                label="Sort columns"
                value={mySortBy}
                onChange={(e) => setMySortBy(e.target.value)}
              >
                {MY_SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: "none", md: "block" },
              alignSelf: "stretch",
              borderColor: "#E8EAED",
              my: 0.25,
            }}
          />
          <Divider
            sx={{
              display: { xs: "block", md: "none" },
              borderColor: "#E8EAED",
            }}
          />

          <Box
            sx={{
              flex: { md: "1 1 auto" },
              minWidth: 0,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1, sm: 1.25 },
              px: { xs: 1.25, sm: 1.5 },
              py: { xs: 1, sm: 1 },
              borderRadius: 1.5,
              bgcolor: "#fff",
              border: "1px solid #F0F1F3",
            }}
          >
            <Tooltip title="Outline best ROI and profit cells in the table below" arrow placement="top">
              <Typography
                variant="caption"
                component="span"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  letterSpacing: 0.03,
                  fontSize: 11,
                  textTransform: "uppercase",
                  width: { xs: "100%", sm: "auto" },
                  mb: { xs: -0.25, sm: 0 },
                }}
              >
                Highlight in table
              </Typography>
            </Tooltip>
            <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={highlightBestRoi}
                    onChange={(e) => setHighlightBestRoi(e.target.checked)}
                    sx={{ py: 0 }}
                  />
                }
                label="Highest ROI"
                sx={{
                  m: 0,
                  mr: 1,
                  "& .MuiTypography-root": { fontSize: 13, fontWeight: 500 },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={highlightBestProfit}
                    onChange={(e) => setHighlightBestProfit(e.target.checked)}
                    sx={{ py: 0 }}
                  />
                }
                label="Highest profit"
                sx={{
                  m: 0,
                  "& .MuiTypography-root": { fontSize: 13, fontWeight: 500 },
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {compareSource === COMPARE_SOURCE.myScenarios &&
        orderedScenarios.length === 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have one saved scenario below. Save another to compare two or more side by side.
          </Alert>
        )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: "1px solid #F2F4F7", borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#FAFBFC" }}>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  minWidth: 160,
                  verticalAlign: "top",
                }}
              >
                Metric
              </TableCell>
              {orderedScenarios.map((s, idx) => (
                <TableCell
                  key={s.id}
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    textAlign: "center",
                    minWidth: 140,
                    verticalAlign: "top",
                  }}
                >
                  <Typography fontWeight={700} fontSize={13}>
                    {s.name}
                  </Typography>
                  <Box display="flex" justifyContent="center">
                    {scenarioCompareChip(s, idx, compareSource)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {metricRows.map((row) => (
              <MetricRow key={row.label} scenarios={orderedScenarios} {...row} />
            ))}
            <TableRow hover>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  minWidth: 160,
                  verticalAlign: "top",
                }}
              >
                Warnings
              </TableCell>
              {orderedScenarios.map((s) => {
                const normalized = normalizeWarnings(s.warnings || []);
                return (
                  <TableCell key={s.id} sx={{ textAlign: "center", verticalAlign: "top" }}>
                    {normalized.length === 0 ? (
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 13 }}>
                        None
                      </Typography>
                    ) : (
                      <Stack spacing={0.5} alignItems="center">
                        {normalized.map((w, i) => (
                          <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} compact />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
